import React, { useState } from 'react';
import axios from 'axios';
import { CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { COMPANY, CONTACT_INFO } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', company: '', subject: '', question: '' });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = [['name', 'Name'], ['email', 'Email'], ['subject', 'Subject'], ['question', 'Question']];
    for (const [key, label] of required) {
      if (!form[key] || !String(form[key]).trim()) {
        toast({ title: `${label} is required`, description: `Please fill in the "${label}" field to continue.`, variant: 'destructive' });
        return;
      }
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast({ title: 'Invalid email', description: 'Please enter a valid email address.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form, { timeout: 60000 });
      setSubmitted(true);
      toast({ title: 'Message sent', description: "We'll get back to you as soon as possible." });
      setForm({ name: '', phone: '', email: '', company: '', subject: '', question: '' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: err?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-800 outline-none transition-colors focus:border-[#3a9b5c] focus:ring-2 focus:ring-[#3a9b5c]/20';
  const labelCls = 'mb-1.5 block text-sm font-medium text-gray-700';

  return (
    <div>
      <section className="ea-hero-gradient py-16">
        <div className="mx-auto max-w-7xl px-5 text-center lg:px-8">
          <h1 className="font-display text-4xl font-extrabold text-white sm:text-5xl">Contact Us</h1>
          <p className="mt-4 text-lg text-white/85">Contact us about anything related to our company or services.</p>
        </div>
      </section>

      <section className="bg-[#e9f3ec] py-16">
        <div className="mx-auto grid max-w-6xl gap-10 px-5 lg:grid-cols-3 lg:px-8">
          <div className="lg:col-span-2">
            {submitted ? (
              <div className="flex h-full flex-col items-center justify-center rounded-2xl bg-white p-10 text-center shadow-md">
                <CheckCircle2 className="h-14 w-14 text-[#3a9b5c]" />
                <h3 className="ea-heading mt-4 text-xl font-bold">Thanks for reaching out!</h3>
                <p className="mt-2 text-gray-600">We’ll do our best to get back to you as soon as possible.</p>
                <button onClick={() => setSubmitted(false)} className="mt-6 rounded-full bg-[#3a9b5c] px-6 py-2.5 text-sm font-semibold text-white">Send another message</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5 rounded-2xl bg-white p-8 shadow-md">
                <p className="text-sm text-gray-500">We'll do our best to get back to you as soon as possible.</p>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div><label className={labelCls}>Name *</label><input className={inputCls} value={form.name} onChange={(e) => update('name', e.target.value)} /></div>
                  <div><label className={labelCls}>Phone Number</label><input className={inputCls} value={form.phone} onChange={(e) => update('phone', e.target.value)} /></div>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div><label className={labelCls}>Email *</label><input type="email" className={inputCls} value={form.email} onChange={(e) => update('email', e.target.value)} /></div>
                  <div><label className={labelCls}>Company</label><input className={inputCls} value={form.company} onChange={(e) => update('company', e.target.value)} /></div>
                </div>
                <div><label className={labelCls}>Subject *</label><input className={inputCls} value={form.subject} onChange={(e) => update('subject', e.target.value)} /></div>
                <div><label className={labelCls}>Question *</label><textarea rows={5} className={inputCls} value={form.question} onChange={(e) => update('question', e.target.value)} /></div>
                <button type="submit" disabled={submitting} className="rounded-full bg-[#3a9b5c] px-8 py-3 font-semibold text-white shadow-md transition-all hover:bg-[#318150] disabled:opacity-60">{submitting ? 'Sending...' : 'Submit'}</button>
              </form>
            )}
          </div>

          <div>
            <div className="rounded-2xl bg-[#163d59] p-8 text-white">
              <div className="flex flex-col items-center gap-4 text-center">
                <img src={COMPANY.logo} alt="E.A. Lead Solutions" className="h-24 w-auto rounded-xl bg-white object-contain p-3" />
                <h3 className="font-display text-lg font-bold italic leading-snug text-[#7cc4a0]">
                  "We do it right or don't do it at all"
                </h3>
              </div>
              <ul className="mt-6 space-y-5 text-sm">
                {CONTACT_INFO.map((c, i) => {
                  const Icon = c.icon;
                  return (
                    <li key={i} className="flex items-start gap-3 text-white/85">
                      <Icon size={18} className="mt-0.5 shrink-0 text-[#7cc4a0]" /> {c.text}
                    </li>
                  );
                })}
              </ul>
              <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(COMPANY.whatsappMsg)}`} target="_blank" rel="noopener noreferrer" className="mt-7 inline-block rounded-full bg-[#25D366] px-6 py-2.5 text-sm font-semibold text-white">Chat on WhatsApp</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
